import React, { useEffect, useMemo } from 'react';
import { ErrorMessage, Grid } from 'ui-toolkit';
import { useSelector } from 'react-redux';
import { RootStore } from '../../../store/types';
import { getAdminDocuments } from '../../../store/order/documents/adminDocuments/dispatch';
import { getTitleReport } from '../../../store/order/titleInformation/dispatch';
import { getRedVisionSearch, getTitleSearchRequest } from '../../../store/order/titleSearchRequests/dispatch';
import { TitleSearchReportContainer } from './TitleSearchReportContainer';
import { TitleSearchContainer } from './TitleSearchContainer';
import { getDiscounts } from '../../../store/order/discounts';
import { TitlePolicyPremiumDetailsContainer } from '../../../components/order/title/TitlePolicyPremiumDetails/TitlePolicyPremiumDetailsContainer';
import { TitlePolicyContainer } from './titlePolicy/TitlePolicyContainer';
import { ReissueDiscountAlert } from 'components/order/discounts/ReissueDiscountAlert';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../types/shared/UUID';
import { TitleOwnershipContainer } from '../../../components/order/title/transferDeed/TitleOwnershipContainer';
import { useSetOrderNavigationTab } from '../../../utilities/hooks/useSetOrderNavigationTab';
import { IOrderNavigationSlice, OrderTabs } from '../../../store/order/orderNavigation/orderNavigationSlice';
import { useGetOrderQuery } from '../../../API/order/OrderApi';
import { getTaxCertificationRequestAsyncAction } from '../../../store/order/taxCertifications/reducer';
import { useAppDispatch } from '../../../store/store';

const titleContainerNavigationOptions: IOrderNavigationSlice = { tab: OrderTabs.Title };
export const TitleContainer: React.FC = () => {
  const { orderId } = useParams<{ orderId: UUID }>();
  useSetOrderNavigationTab(titleContainerNavigationOptions);

  const dispatch = useAppDispatch();
  const { documentsError, deleteDocumentsError, documentsActionError } = useSelector(
    ({ order }: RootStore) => order.documents.adminDocuments
  );
  const { titleReportError } = useSelector(({ order }: RootStore) => order.titleReport);
  const { payoffRequestError } = useSelector(({ order }: RootStore) => order.payoffRequests);
  const { fetchError: taxCertificationRequestError } = useSelector(
    ({ order }: RootStore) => order.taxCertification.taxCertificationRequestsSlice
  );
  const { titleSearchRequestError, redVisionSearchError } = useSelector(
    ({ order }: RootStore) => order.titleSearchRequests
  );
  const { data: order } = useGetOrderQuery(orderId);
  useEffect(() => {
    dispatch(getAdminDocuments(orderId));
    dispatch(getTitleSearchRequest(orderId));
    dispatch(getRedVisionSearch(order));
    dispatch(getDiscounts(orderId));
    if (order?.transaction?.titleCommitmentId) {
      dispatch(getTitleReport(orderId));
    }
    if (order?.transaction.taxCertificationId) {
      dispatch(getTaxCertificationRequestAsyncAction(orderId));
    }
  }, [orderId, dispatch, order]);

  const pageError = useMemo(
    () =>
      documentsError ||
      titleReportError ||
      titleSearchRequestError ||
      redVisionSearchError ||
      deleteDocumentsError ||
      documentsActionError ||
      payoffRequestError ||
      taxCertificationRequestError,
    [
      documentsError,
      titleReportError,
      titleSearchRequestError,
      redVisionSearchError,
      deleteDocumentsError,
      documentsActionError,
      payoffRequestError,
      taxCertificationRequestError,
    ]
  );

  const errorMessage = useMemo(() => {
    if (typeof pageError === 'string') {
      return pageError;
    }
    return pageError?.message;
  }, [pageError]);

  return (
    <Grid>
      <ReissueDiscountAlert />
      <ErrorMessage message={errorMessage} />
      <Grid columnCount={2}>
        <Grid>
          <TitleSearchReportContainer orderId={orderId} />
          <TitleOwnershipContainer />
          <TitleSearchContainer orderId={orderId} />
        </Grid>
        <Grid>
          <TitlePolicyContainer />
          <TitlePolicyPremiumDetailsContainer />
        </Grid>
      </Grid>
    </Grid>
  );
};
